// GoogleMapDrawCircle.js
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './GoogleMapDrawCircle.module.css'; // Import the CSS module

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export default function GoogleMapDrawCircle({ onDone, onClose }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const circleRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const justSelectedRef = useRef(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { t } = useTranslation();

  // No need for windowWidth state and its useEffect anymore

  useEffect(() => {
    const initMap = () => {
      if (!window.google || !mapRef.current) {
        console.warn("Google Maps API not loaded or map container not ready. Retrying...");
        return;
      }

      const map = new window.google.maps.Map(mapRef.current, {
        center: { lat: 46.8182, lng: 8.2275 },
        zoom: 8, // Default zoom level
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        scaleControl: true,
      });
      mapInstanceRef.current = map;

      const circle = new window.google.maps.Circle({
        strokeColor: '#4285F4',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#4285F4',
        fillOpacity: 0.2,
        map,
        center: map.getCenter(),
        radius: 5000, // Reduced from 10000m (10km) to 5000m (5km) for better usability
        editable: true,
        draggable: true,
      });

      circleRef.current = circle;

      if (window.google && window.google.maps && window.google.maps.places) {
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        placesServiceRef.current = new window.google.maps.places.PlacesService(mapInstanceRef.current);
        console.log("Google Maps Places services initialized.");
      } else {
        console.error("Google Maps Places library NOT loaded or not available on window.google.maps.places.");
      }
    };

    const checkGoogleAndInit = setInterval(() => {
      if (window.google && window.google.maps) {
        clearInterval(checkGoogleAndInit);
        initMap();
      }
    }, 100);

    // Cleanup function
    return () => {
      clearInterval(checkGoogleAndInit);
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
      autocompleteServiceRef.current = null;
      placesServiceRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    if (debouncedSearchTerm) {
      console.log("Searching for:", debouncedSearchTerm);
      if (autocompleteServiceRef.current) {
        autocompleteServiceRef.current.getPlacePredictions(
          {
            input: debouncedSearchTerm,
            componentRestrictions: { country: 'ch' },
          },
          (predictions, status) => {
            console.log("Autocomplete Status:", status, "Predictions:", predictions);
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              setSearchResults(predictions);
            } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              setSearchResults([]);
              console.log("No results found for:", debouncedSearchTerm);
            } else {
              setSearchResults([]);
              console.error("Autocomplete error:", status);
            }
          }
        );
      } else {
        console.warn("autocompleteServiceRef.current is NULL. Places library might not be loaded yet.");
      }
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearchTerm]);

  const handleSelectSearchResult = useCallback((prediction) => {
    if (!mapInstanceRef.current || !placesServiceRef.current) {
      console.error("Map or Places Service not ready for search result selection.");
      return;
    }

    placesServiceRef.current.getDetails(
      { placeId: prediction.place_id, fields: ['geometry', 'formatted_address'] },
      (place, status) => {
        console.log("Place Details Status:", status, "Place:", place);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place && place.geometry) {
          if (place.geometry.viewport) {
            mapInstanceRef.current.fitBounds(place.geometry.viewport);
          } else {
            mapInstanceRef.current.setCenter(place.geometry.location);
            mapInstanceRef.current.setZoom(13); // Increased from 12 to 13 for better detail when searching
          }
          if (circleRef.current) {
            circleRef.current.setCenter(place.geometry.location);
          }
        } else {
          console.error("Error getting place details or geometry:", status, place);
        }
      }
    );
    justSelectedRef.current = true;
    setSearchTerm(prediction.description);
    setSearchResults([]);
  }, []);

  const samplePointsInCircle = useCallback((center, radiusMeters, count = 8) => {
    if (!center || typeof center.lat !== 'function' || typeof center.lng !== 'function') {
      console.error("Invalid center object provided to samplePointsInCircle. Expected google.maps.LatLng.");
      return [];
    }

    const centerLat = center.lat();
    const centerLng = center.lng();

    if (isNaN(centerLat) || isNaN(centerLng)) {
      console.error("Center coordinates are NaN in samplePointsInCircle. Cannot generate points.");
      return [];
    }

    if (isNaN(radiusMeters) || radiusMeters <= 0) {
      console.warn("Invalid or zero radius in samplePointsInCircle:", radiusMeters, ". Returning no sample points or only center if 0.");
      return radiusMeters === 0 ? [new window.google.maps.LatLng(centerLat, centerLng)] : [];
    }

    const points = [new window.google.maps.LatLng(centerLat, centerLng)];
    const EARTH_RADIUS_METERS = 6371000;

    for (let i = 0; i < count; i++) {
      const angle = (2 * Math.PI * i) / count;
      const latRad = centerLat * (Math.PI / 180);
      const cosLatRad = Math.cos(latRad);

      const latOffset = (radiusMeters * Math.sin(angle)) / EARTH_RADIUS_METERS * (180 / Math.PI);
      let lngOffset = 0;

      if (Math.abs(cosLatRad) > 1e-6) {
        lngOffset = (radiusMeters * Math.cos(angle)) / (EARTH_RADIUS_METERS * cosLatRad) * (180 / Math.PI);
      }

      const newLat = centerLat + latOffset;
      const newLng = centerLng + lngOffset;

      if (isNaN(newLat) || isNaN(newLng) || newLat > 90 || newLat < -90 || newLng > 180 || newLng < -180) {
        console.warn("Generated sample point is invalid (NaN or out of geographical bounds):", { newLat, newLng });
        continue;
      }
      points.push(new window.google.maps.LatLng(newLat, newLng));
    }
    return points;
  }, []);

  const handleConfirm = useCallback(async () => {
    const circle = circleRef.current;
    if (!circle) {
      console.error("Circle object not found. Cannot confirm area.");
      if (onDone) onDone([]);
      if (onClose) onClose();
      return;
    }

    const center = circle.getCenter();
    const radius = circle.getRadius();

    if (!center || typeof center.lat !== 'function' || typeof center.lng !== 'function' || isNaN(center.lat()) || isNaN(center.lng())) {
      console.error("Invalid circle center coordinates. Cannot confirm area.");
      if (onDone) onDone([]);
      if (onClose) onClose();
      return;
    }
    if (isNaN(radius) || radius <= 0) {
      console.error("Circle radius is invalid (NaN or non-positive). Cannot confirm area.");
      if (onDone) onDone([]);
      if (onClose) onClose();
      return;
    }

    const samplePoints = samplePointsInCircle(center, radius);

    if (samplePoints.length === 0) {
      console.warn("No valid sample points generated for geocoding. Cannot confirm area.");
      if (onDone) onDone([], center.toJSON(), radius);
      if (onClose) onClose();
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    const areasSet = new Set();
    const areas = [];

    const geocodePromises = samplePoints.map(point =>
      new Promise(async (resolve) => {
        if (!point || !(point instanceof window.google.maps.LatLng) || isNaN(point.lat()) || isNaN(point.lng())) {
          console.warn("Skipping geocode for an invalid or non-LatLng point:", point);
          resolve(null);
          return;
        }
        try {
          const { results } = await geocoder.geocode({ location: point });
          resolve(results?.[0] || null);
        } catch (err) {
          console.warn(`Geocode failed for point ${point.toString()}:`, err);
          resolve(null);
        }
      })
    );

    const settledResults = await Promise.allSettled(geocodePromises);

    settledResults.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        const place = result.value;
        const comps = place.address_components;

        const getComp = (type) =>
          comps.find((c) => c.types.includes(type))?.long_name || '';
        const getShortComp = (type) =>
          comps.find((c) => c.types.includes(type))?.short_name || '';

        const zip = getComp('postal_code');
        // Improved city detection: Try multiple fallbacks for better accuracy
        const city = getComp('locality') || 
                     getComp('postal_town') || 
                     getComp('sublocality') || 
                     getComp('sublocality_level_1') || 
                     getComp('administrative_area_level_3') || 
                     getComp('administrative_area_level_2');
        const country = getComp('country');
        const countryISO = getShortComp('country');

        // Debug logging to help troubleshoot city detection
        console.log(`Sample point ${index + 1} geocoding:`, {
          zip,
          city,
          locality: getComp('locality'),
          postal_town: getComp('postal_town'),
          sublocality: getComp('sublocality'),
          admin_level_2: getComp('administrative_area_level_2'),
          admin_level_3: getComp('administrative_area_level_3'),
          country,
          countryISO
        });

        const key = `${zip}|${city}|${country}|${countryISO}`;
        if (zip && city && country && countryISO && !areasSet.has(key)) {
          areasSet.add(key);
          areas.push({ zip, city, country, countryISO });
          console.log(`✅ Added area: ${city} (${zip})`);
        } else if (!zip || !city || !country || !countryISO) {
          console.warn(`⚠️ Skipping incomplete area data:`, { zip, city, country, countryISO });
        }
      }
    });

    if (onDone) {
      onDone(areas, center.toJSON(), radius);
    } else {
      console.warn("onDone prop not provided to GoogleMapDrawCircle.");
    }
    if (onClose) {
      onClose();
    }
  }, [onDone, onClose, samplePointsInCircle]);


  if (!window.google) {
    return (
      <div className={styles.container} style={{ padding: '20px', textAlign: 'center', fontSize: '1.2em' }}>
        {t('loading_Maps_api')}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>{t('Draw_Circle')}</h3>
      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder={t('Search_Location')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
        {searchResults.length > 0 && (
          <ul className={styles.searchResultsDropdown}>
            {searchResults.map((prediction) => (
              <li
                key={prediction.place_id}
                onClick={() => handleSelectSearchResult(prediction)}
                className={styles.searchResultItem}
              >
                {prediction.description}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div ref={mapRef} className={styles.mapContainer} />
      <button
        onClick={handleConfirm}
        className={styles.confirmButton}
      >
        {t('Confirm')}
      </button>
    </div>
  );
}